"use client";
import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Pin,
  PinOff,
  Search,
  Filter as FilterIcon,
  ArrowUpDown,
  Ship,
  User,
  Truck,
} from "lucide-react";
import {
  useFetchProviders,
  useFetchRequesters,
} from "../../../hooks/useFetchRoute";
import LoadingComponent from "../../../components/form-components/LoadingComponent";
import { Transport } from "../../../types/Route";

const useClickOutside = (
  ref: React.RefObject<HTMLDivElement>,
  handler: (event: MouseEvent | TouchEvent) => void,
) => {
  useEffect(() => {
    const listener = (event: MouseEvent | TouchEvent) => {
      if (!ref.current || ref.current.contains(event.target as Node)) {
        return;
      }
      handler(event);
    };
    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);
    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [ref, handler]);
};

const TransportsPage: React.FC = () => {
  const {
    providers,
    loading: providersLoading,
    error: providersError,
  } = useFetchProviders();
  const {
    requesters,
    loading: requestersLoading,
    error: requestersError,
  } = useFetchRequesters();

  const [selectedTab, setSelectedTab] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState<{
    key: keyof Transport;
    direction: "asc" | "desc";
  }>({ key: "schedule", direction: "desc" });
  const [searchTerm, setSearchTerm] = useState("");
  const [searchField, setSearchField] = useState<keyof Transport>("from");
  const [pinnedRows, setPinnedRows] = useState<string[]>([]);
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);
  const [isSearchFieldMenuOpen, setIsSearchFieldMenuOpen] = useState(false);

  const filterMenuRef = useRef<HTMLDivElement>(null);
  const sortMenuRef = useRef<HTMLDivElement>(null);
  const searchFieldMenuRef = useRef<HTMLDivElement>(null);
  useClickOutside(filterMenuRef, () => setIsFilterMenuOpen(false));
  useClickOutside(sortMenuRef, () => setIsSortMenuOpen(false));
  useClickOutside(searchFieldMenuRef, () => setIsSearchFieldMenuOpen(false));

  const allTransports: Transport[] = useMemo(() => {
    const combined = [...(providers || []), ...(requesters || [])];
    return combined.map((item, index) => ({
      ...item,
      _id: item._id || `${item.requestType}-${index}`,
    }));
  }, [providers, requesters]);

  const handlePin = (id: string) => {
    setPinnedRows((prev) =>
      prev.includes(id)
        ? prev.filter((rowId) => rowId !== id)
        : prev.length < 4
          ? [...prev, id]
          : prev,
    );
  };

  const { pinned, unpinned } = useMemo(() => {
    const pinnedData = allTransports.filter((t) => pinnedRows.includes(t._id));
    const unpinnedData = allTransports.filter(
      (t) => !pinnedRows.includes(t._id),
    );
    return { pinned: pinnedData, unpinned: unpinnedData };
  }, [allTransports, pinnedRows]);

  const filteredTransports = useMemo(() => {
    let filtered = unpinned;
    if (selectedTab !== "All") {
      filtered = unpinned.filter(
        (t) => t.requestType.toLowerCase() === selectedTab.toLowerCase(),
      );
    }
    if (searchTerm) {
      filtered = filtered.filter((t) => {
        const value = t[searchField];
        if (typeof value === "string") {
          return value.toLowerCase().includes(searchTerm.toLowerCase());
        } else if (
          searchField === "userId" &&
          typeof value === "object" &&
          value !== null &&
          "firstName" in value
        ) {
          const name = `${value.firstName} ${value.lastName || ""}`;
          return name.toLowerCase().includes(searchTerm.toLowerCase());
        }
        return false;
      });
    }
    return filtered;
  }, [unpinned, selectedTab, searchTerm, searchField]);

  const sortedAndPaginatedTransports = useMemo(() => {
    const sorted = [...filteredTransports].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue === undefined || aValue === null) return 1;
      if (bValue === undefined || bValue === null) return -1;

      let comparison = 0;
      if (sortConfig.key === "schedule") {
        comparison =
          new Date(aValue as string).getTime() -
          new Date(bValue as string).getTime();
      } else if (sortConfig.key === "userId") {
        const nameA =
          (a.userId?.firstName || "") + " " + (a.userId?.lastName || "");
        const nameB =
          (b.userId?.firstName || "") + " " + (b.userId?.lastName || "");
        comparison = nameA.localeCompare(nameB);
      } else if (typeof aValue === "string" && typeof bValue === "string") {
        comparison = aValue.localeCompare(bValue);
      }

      return sortConfig.direction === "asc" ? comparison : -comparison;
    });
    const startIndex = (currentPage - 1) * 10;
    const paginated = sorted.slice(startIndex, startIndex + 10);
    return [...pinned, ...paginated];
  }, [filteredTransports, currentPage, sortConfig, pinned]);

  const totalPages = Math.ceil(filteredTransports.length / 10);

  const handleSort = (key: keyof Transport) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
    setIsSortMenuOpen(false);
  };

  const handleFilter = (type: string) => {
    setSelectedTab(type);
    setCurrentPage(1);
    setIsFilterMenuOpen(false);
  };

  const searchOptions: { value: keyof Transport; label: string }[] = [
    { value: "from", label: "Starting Point" },
    { value: "to", label: "Ending Point" },
    { value: "userId", label: "Name" },
    { value: "selfVehicle", label: "Vehicle Type" },
    { value: "userLocation", label: "Current Location" },
  ];

  return (
    <div className="p-4 min-h-screen animate-fadeIn">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
        <TransportCard
          title="All Routes"
          count={allTransports.length}
          icon={<Ship className="text-blue-500" size={28} />}
          color="border-blue-500"
        />
        <TransportCard
          title="Providers"
          count={providers?.length || 0}
          icon={<Truck className="text-green-500" size={28} />}
          color="border-green-500"
        />
        <TransportCard
          title="Requesters"
          count={requesters?.length || 0}
          icon={<User className="text-purple-500" size={28} />}
          color="border-purple-500"
        />
      </div>
      <div className="bg-white rounded-lg shadow p-4 animate-slideUp">
        <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-2 flex-grow sm:flex-grow-0">
            <div className="relative">
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border rounded-lg w-full"
              />
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>
            <div className="relative" ref={searchFieldMenuRef}>
              <button
                onClick={() => setIsSearchFieldMenuOpen((p) => !p)}
                className="p-2 border rounded-md hover:bg-gray-100 w-40 text-left flex justify-between items-center"
              >
                <span>
                  {searchOptions.find((o) => o.value === searchField)?.label}
                </span>
                <ChevronRight
                  size={16}
                  className={`transform transition-transform ${
                    isSearchFieldMenuOpen ? "rotate-90" : ""
                  }`}
                />
              </button>
              {isSearchFieldMenuOpen && (
                <div className="absolute left-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border">
                  {searchOptions.map(({ value, label }) => (
                    <button
                      key={value}
                      onClick={() => {
                        setSearchField(value);
                        setIsSearchFieldMenuOpen(false);
                      }}
                      className={`block w-full text-left px-4 py-2 text-sm ${
                        searchField === value ? "bg-gray-100" : ""
                      } hover:bg-gray-100`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative" ref={filterMenuRef}>
              <button
                onClick={() => setIsFilterMenuOpen((p) => !p)}
                className="p-2 rounded-md hover:bg-gray-100"
              >
                <FilterIcon size={20} />
              </button>
              {isFilterMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border">
                  {["All", "Provider", "Requester"].map((tab) => (
                    <button
                      key={tab}
                      onClick={() => handleFilter(tab)}
                      className={`block w-full text-left px-4 py-2 text-sm ${
                        selectedTab === tab ? "bg-gray-100" : ""
                      } hover:bg-gray-100`}
                    >
                      {tab}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="relative" ref={sortMenuRef}>
              <button
                onClick={() => setIsSortMenuOpen((p) => !p)}
                className="p-2 rounded-md hover:bg-gray-100"
              >
                <ArrowUpDown size={20} />
              </button>
              {isSortMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border">
                  <button
                    onClick={() => handleSort("userId")}
                    className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                  >
                    Sort by Name
                  </button>
                  <button
                    onClick={() => handleSort("from")}
                    className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                  >
                    Sort by Start Point
                  </button>
                  <button
                    onClick={() => handleSort("to")}
                    className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                  >
                    Sort by Ending Point
                  </button>
                  <button
                    onClick={() => handleSort("userLocation")}
                    className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                  >
                    Sort by Current Location
                  </button>
                  <button
                    onClick={() => handleSort("schedule")}
                    className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                  >
                    Sort by Schedule
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        {providersLoading || requestersLoading ? (
          <LoadingComponent />
        ) : (
          <TransportTable
            transports={sortedAndPaginatedTransports}
            error={providersError || requestersError}
            pinnedRows={pinnedRows}
            onPin={handlePin}
          />
        )}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          totalEntries={filteredTransports.length}
        />
      </div>
    </div>
  );
};

const TransportCard: React.FC<{
  title: string;
  count: number;
  icon: React.ReactNode;
  color: string;
}> = ({ title, count, icon, color }) => (
  <div
    className={`bg-white rounded-lg shadow p-4 flex items-center space-x-4 transition-all duration-300 transform hover:scale-105 hover:shadow-lg border-l-4 ${color}`}
  >
    <div className="flex-shrink-0">{icon}</div>
    <div>
      <p className="text-sm font-medium text-gray-500">{title}</p>
      <h2 className="text-2xl font-bold text-gray-800">{count}</h2>
    </div>
  </div>
);

const TransportTable: React.FC<{
  transports: Transport[];
  error: string | null;
  pinnedRows: string[];
  onPin: (id: string) => void;
}> = ({ transports, error, pinnedRows, onPin }) => {
  const getSourceClass = (source: string, isPinned: boolean) => {
    if (isPinned) return "bg-yellow-100 hover:bg-yellow-200/60";
    switch (source) {
      case "provider":
        return "bg-green-50/50 hover:bg-green-100/60";
      case "requester":
        return "bg-purple-50/50 hover:bg-purple-100/60";
      default:
        return "hover:bg-gray-50";
    }
  };

  return (
    <div className="overflow-x-auto">
      {error ? (
        <div className="text-red-500 p-4">Error: {error}</div>
      ) : transports.length === 0 ? (
        <div className="text-gray-500 p-4 text-center">No data available</div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-gray-50">
              <th className="text-left p-3 font-semibold text-gray-600">Pin</th>
              <th className="text-left p-3 font-semibold text-gray-600">
                Name
              </th>
              <th className="text-left p-3 font-semibold text-gray-600">
                Starting Point
              </th>
              <th className="text-left p-3 font-semibold text-gray-600">
                Ending Point
              </th>
              <th className="text-left p-3 font-semibold text-gray-600">
                Vehicle Type
              </th>
              <th className="text-left p-3 font-semibold text-gray-600">
                Current Location
              </th>
              <th className="text-left p-3 font-semibold text-gray-600">
                Schedule
              </th>
              <th className="text-left p-3 font-semibold text-gray-600">
                Type
              </th>
            </tr>
          </thead>
          <tbody>
            {transports.map((transport) => (
              <tr
                key={transport._id}
                className={`border-b animate-fadeIn transition-colors duration-200 ${getSourceClass(
                  transport.requestType,
                  pinnedRows.includes(transport._id),
                )}`}
              >
                <td
                  className="p-3 cursor-pointer text-gray-500 hover:text-yellow-500"
                  onClick={() => onPin(transport._id)}
                >
                  {pinnedRows.includes(transport._id) ? (
                    <PinOff size={18} className="text-yellow-600" />
                  ) : (
                    <Pin size={18} />
                  )}
                </td>
                <td className="p-3">{`${transport.userId?.firstName || ""} ${
                  transport.userId?.lastName || ""
                }`}</td>
                <td className="p-3">{transport.from}</td>
                <td className="p-3">{transport.to}</td>
                <td className="p-3">
                  {transport.selfVehicle ?? transport.vehicle ?? "Any"}
                </td>
                <td className="p-3">{transport.userLocation ?? "N/A"}</td>
                <td className="p-3">
                  {transport.schedule
                    ? new Date(transport.schedule).toLocaleString()
                    : "N/A"}
                </td>
                <td className="p-3 capitalize">{transport.requestType}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

const Pagination: React.FC<{
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  totalEntries: number;
}> = ({ currentPage, totalPages, onPageChange, totalEntries }) => (
  <div className="flex flex-col sm:flex-row justify-between items-center mt-4">
    <p className="text-sm text-gray-600 mb-2 sm:mb-0">
      Showing {(currentPage - 1) * 10 + 1} to{" "}
      {Math.min(currentPage * 10, totalEntries)} of {totalEntries} entries
    </p>
    <div className="flex space-x-2">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="px-3 py-1 rounded border hover:bg-gray-100 disabled:opacity-50"
      >
        <ChevronLeft size={18} />
      </button>
      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
        <button
          key={page}
          onClick={() => onPageChange(page)}
          className={`px-3 py-1 rounded border ${
            currentPage === page
              ? "bg-[#3c0b0b] text-white"
              : "hover:bg-gray-100"
          }`}
        >
          {page}
        </button>
      ))}
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="px-3 py-1 rounded border hover:bg-gray-100 disabled:opacity-50"
      >
        <ChevronRight size={18} />
      </button>
    </div>
  </div>
);

export default TransportsPage;
